// SMS onboarding UI — the operator's lane picker (Quick / Dedicated / Legacy),
// the Quick-lane number provisioner, the cancel (park / teardown) controls, and the
// super-admin master-account vault. Talks to the endpoints shipped in #474/#476/#478.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Zap, IdCard, KeyRound, Trash2, PauseCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from './Toast';

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
async function jget(url: string) {
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function jsend(url: string, method: string, body?: any) {
  const opts: any = { method, headers: { ...authHeaders() } };
  if (body !== undefined && method !== 'GET') { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
  const r = await fetch(url, opts);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || r.statusText);
  return j;
}

const F = ({ label, children }: any) => (
  <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>
);

// ── Lane picker ──────────────────────────────────────────────────────────────
const LANES = [
  { key: 'quick', label: 'Quick', icon: Zap, blurb: 'New number on the C2G account, texts instantly under the shared campaign.' },
  { key: 'dedicated', label: 'Dedicated', icon: IdCard, blurb: "Client's own brand + campaign — their business name is the sender." },
  { key: 'legacy', label: 'Legacy', icon: KeyRound, blurb: 'Client already runs their own Twilio — enter their credentials.' },
];

export function SmsLanePicker({ lane, onChange }: { lane: string; onChange: (l: any) => void }) {
  return (
    <div className="card p-4">
      <div className="text-sm font-medium mb-3">How should this client text?</div>
      <div className="grid grid-cols-3 gap-3">
        {LANES.map(l => {
          const Icon = l.icon; const active = lane === l.key;
          return (
            <button key={l.key} type="button" onClick={() => onChange(l.key)}
              className={`text-left rounded-lg border p-3 transition ${active ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}>
              <div className="flex items-center gap-2 font-medium text-sm"><Icon size={15} className={active ? 'text-primary' : 'text-textSecondary'} />{l.label}</div>
              <div className="text-xs text-textSecondary mt-1 leading-snug">{l.blurb}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Quick lane: buy a new number or transfer one, onto the shared campaign ──────
export function QuickLane({ domainUuid, webhook, onDone }: { domainUuid?: string; webhook?: string; onDone?: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<'new' | 'transfer'>('new');
  const [areaCode, setAreaCode] = useState('');
  const [pnSid, setPnSid] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [found, setFound] = useState<any[] | null>(null);   // available-number search results (#492)
  const [picked, setPicked] = useState('');
  const [searching, setSearching] = useState(false);

  if (!domainUuid) {
    return <div className="card p-6 text-sm text-textSecondary">Save the trunk first, then provision a texting number here.</div>;
  }

  async function provision() {
    setBusy(true); setResult(null);
    try {
      const body: any = { domain_uuid: domainUuid, mode };
      if (mode === 'new' && areaCode) body.area_code = areaCode;
      if (mode === 'new' && picked) body.phone_number = picked;   // buy the number the admin chose
      if (mode === 'transfer') body.phone_number_sid = pnSid.trim();
      const r = await jsend('/api/sms/quick-provision', 'POST', body);
      setResult(r);
      toast('success', `Provisioned ${r.phone_number}${r.campaign_attached ? ' · on campaign' : ' · campaign attach pending'}`);
      onDone && onDone();
    } catch (e: any) { toast('error', e.message); }
    finally { setBusy(false); }
  }

  async function search() {
    setSearching(true); setFound(null); setPicked('');
    try {
      const r = await jsend(`/api/sms/master/available${areaCode ? `?area_code=${areaCode}` : ''}`, 'GET');
      setFound(r.numbers || []);
    } catch (e: any) { toast('error', e.message); }
    finally { setSearching(false); }
  }

  return (
    <div className="card p-6 space-y-4">
      <div className="flex gap-2 text-sm">
        <button type="button" className={mode === 'new' ? 'btn-primary' : 'btn-ghost'} onClick={() => setMode('new')}>New number</button>
        <button type="button" className={mode === 'transfer' ? 'btn-primary' : 'btn-ghost'} onClick={() => setMode('transfer')}>Transfer existing</button>
      </div>
      {mode === 'new' ? (
        <div className="space-y-3">
          <F label="Area code (optional — uses the account default if blank)">
            <div className="flex gap-2 items-center">
              <input className="input max-w-[160px] font-mono" value={areaCode} maxLength={3}
                onChange={e => { setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3)); setFound(null); setPicked(''); }} placeholder="989" />
              <button type="button" className="btn-ghost" disabled={searching} onClick={search}>{searching ? 'Searching…' : 'Search numbers'}</button>
            </div>
          </F>
          {found && found.length > 0 && (
            <div className="border border-border rounded-lg divide-y divide-border max-h-56 overflow-auto">
              {found.map((n: any) => (
                <button type="button" key={n.phone_number}
                  className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center hover:bg-elevated ${picked === n.phone_number ? 'bg-primary/15' : ''}`}
                  onClick={() => setPicked(n.phone_number)}>
                  <span className="font-mono">{n.phone_number}</span>
                  <span className="text-xs text-textSecondary">{[n.locality, n.region].filter(Boolean).join(', ')}{picked === n.phone_number ? '  ✓' : ''}</span>
                </button>
              ))}
            </div>
          )}
          {found && found.length === 0 && <div className="text-xs text-textSecondary">No available numbers — try a different area code.</div>}
          {picked && <div className="text-xs text-textSecondary">Selected <span className="font-mono text-primary">{picked}</span> — click Buy to provision it.</div>}
        </div>
      ) : (
        <F label="Phone number SID to move onto the C2G account">
          <input className="input font-mono" value={pnSid} onChange={e => setPnSid(e.target.value)} placeholder="PN…" />
        </F>
      )}
      <button type="button" className="btn-primary" disabled={busy || (mode === 'transfer' && !pnSid.trim())} onClick={provision}>
        {busy ? 'Provisioning…' : mode === 'new' ? (picked ? `Buy ${picked} + enable texting` : 'Buy first available + enable texting') : 'Transfer + enable texting'}
      </button>
      {result && (
        <div className="text-sm bg-primary/10 border border-primary/30 rounded-lg p-3 space-y-1">
          <div><span className="text-textSecondary">Number: </span><span className="font-mono">{result.phone_number}</span></div>
          <div className="text-xs text-textSecondary">Joined to the shared C2G campaign{result.campaign_attached ? '' : ' (attach pending — retry if it stays off)'}. Sending is live.</div>
        </div>
      )}
      {webhook && <div className="text-xs text-textSecondary break-all">Inbound webhook: <span className="text-primary">{webhook}</span></div>}
    </div>
  );
}

// ── Dedicated lane: client's OWN brand + campaign in their own subaccount (#501) ──
// Business-identity form -> /dedicated/start runs the pipeline (subaccount, number,
// TrustHub profile, brand, campaign, wire); brand + campaign vetting are async, so
// the stepper polls /dedicated/advance until Twilio approves. Texts then send under
// the client's business name.
const DED_STEPS = [
  { key: 'subaccount', label: 'Subaccount' }, { key: 'number', label: 'Number' },
  { key: 'profile', label: 'Business profile' }, { key: 'brand', label: 'Brand vetting' },
  { key: 'campaign', label: 'Campaign vetting' }, { key: 'wire', label: 'Wire up' },
];
const BUSINESS_TYPES = ['Limited Liability Corporation', 'Corporation', 'Sole Proprietorship', 'Partnership', 'Co-operative', 'Non-profit Corporation'];

export function DedicatedLane({ domainUuid, onDone }: { domainUuid?: string; onDone?: () => void }) {
  const toast = useToast();
  const st = useQuery<any>({
    queryKey: ['sms-dedicated', domainUuid],
    queryFn: () => jsend(`/api/sms/dedicated/status?domain_uuid=${domainUuid}`, 'GET'),
    enabled: !!domainUuid,
  });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [f, setF] = useState<any>({
    legal_name: '', business_type: BUSINESS_TYPES[0], ein: '', website_url: '',
    street: '', city: '', region: '', postal_code: '',
    first_name: '', last_name: '', email: '', phone: '',
    area_code: '', phone_number_sid: '',
  });
  const set = (k: string, v: string) => setF({ ...f, [k]: v });

  if (!domainUuid) {
    return <div className="card p-6 text-sm text-textSecondary">Save the trunk first, then register the client's own texting brand here.</div>;
  }
  const d = st.data || {};
  const started = !!d.started;
  const failed = d.state === 'failed';
  const done = d.state === 'done';

  async function start() {
    setBusy(true); setErrors([]);
    try {
      const body = {
        domain_uuid: domainUuid,
        legal_name: f.legal_name, business_type: f.business_type, ein: f.ein, website_url: f.website_url,
        address: { street: f.street, city: f.city, region: f.region, postal_code: f.postal_code },
        contact: { first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone },
        ...(f.area_code ? { area_code: f.area_code } : {}),
        ...(f.phone_number_sid ? { phone_number_sid: f.phone_number_sid } : {}),
      };
      // raw fetch (not jsend) so a 400 surfaces the FULL field-error list, not just the first
      const resp = await fetch('/api/sms/dedicated/start', { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const r = await resp.json().catch(() => ({}));
      if (!resp.ok) { setErrors(r.errors || [r.error || resp.statusText]); toast('error', 'Registration could not start — see the errors above the button'); return; }
      setEditing(false);
      toast('success', r.next?.step === 'wait' ? 'Registration submitted — Twilio is vetting' : `Pipeline at: ${r.state}`);
      st.refetch(); onDone && onDone();
    } catch (e: any) {
      setErrors([String(e.message || e)]);
      toast('error', 'Registration could not start');
    } finally { setBusy(false); }
  }

  async function advance() {
    setBusy(true);
    try {
      const r = await jsend('/api/sms/dedicated/advance', 'POST', { domain_uuid: domainUuid });
      toast('success', r.next?.step === 'wait' ? `Still vetting (${r.next.waiting_on}: ${r.next.status})` : `Now at: ${r.state}`);
      st.refetch(); if (r.state === 'done') onDone && onDone();
    } catch (e: any) { toast('error', e.message); st.refetch(); }
    finally { setBusy(false); }
  }

  if (done) {
    return (
      <div className="card p-6 text-sm space-y-2">
        <div className="font-medium">Dedicated brand is live</div>
        <div><span className="text-textSecondary">Number: </span><span className="font-mono">{d.phone_number}</span></div>
        <div className="text-xs text-textSecondary">Texts send as <span className="text-primary">{d.business?.legal_name}</span> under their own approved brand + campaign (subaccount <span className="font-mono">{d.subaccount_sid}</span>).</div>
      </div>
    );
  }

  if (started && !editing) {
    const stepIdx = DED_STEPS.findIndex(s => s.key === d.state);
    return (
      <div className="card p-6 space-y-4 text-sm">
        <div className="font-medium">Dedicated brand registration — {d.business?.legal_name || 'in progress'}</div>
        <ol className="space-y-1">
          {DED_STEPS.map((s, i) => {
            const doneStep = stepIdx > i;
            const current = s.key === d.state;
            return (
              <li key={s.key} className={`flex items-center gap-2 ${current ? 'text-primary font-medium' : 'text-textSecondary'}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${doneStep ? 'bg-primary/50' : current ? 'bg-primary animate-pulse' : 'bg-border'}`} />
                <span className={doneStep ? 'line-through' : ''}>{s.label}</span>
                {s.key === 'brand' && d.brand_status ? <span className="text-xs font-mono">({d.brand_status})</span> : null}
                {s.key === 'campaign' && d.campaign_status ? <span className="text-xs font-mono">({d.campaign_status})</span> : null}
              </li>
            );
          })}
        </ol>
        {d.phone_number && <div className="text-xs text-textSecondary">Number: <span className="font-mono">{d.phone_number}</span></div>}
        {d.last_error && (
          <div className="text-xs bg-primary/10 border border-primary/30 rounded-lg p-3 break-all">
            <span className="font-medium">{failed ? 'Registration failed: ' : 'Last error: '}</span>{d.last_error}
          </div>
        )}
        <div className="flex gap-2">
          {!failed && <button type="button" className="btn-primary" disabled={busy} onClick={advance}>{busy ? 'Working…' : d.next?.step === 'wait' ? 'Check vetting status' : 'Continue registration'}</button>}
          {failed && <button type="button" className="btn-primary" disabled={busy} onClick={() => {
            const b = d.business || {};
            setF({ ...f, ...b, ...(b.address || {}), ...(b.contact || {}) });
            setEditing(true);
          }}>Edit &amp; retry</button>}
        </div>
        <p className="text-xs text-textSecondary">Brand and campaign vetting run on Twilio's side and can take hours to a few days. Completed paid steps (subaccount, number, approved brand) are never redone on a retry.</p>
      </div>
    );
  }

  return (
    <div className="card p-6 space-y-4">
      <div className="text-sm font-medium">Dedicated brand — the client's own identity</div>
      <p className="text-xs text-textSecondary">Registers the client's own A2P brand and campaign in their own subaccount, so texts send under <span className="text-primary">their business name</span> (the Kenosha / Stacey pattern). Costs Twilio brand + campaign fees and needs vetting; use Quick if texting as "Computers 2 Go" is fine.</p>
      {errors.length > 0 && (
        <ul className="text-xs bg-primary/10 border border-primary/30 rounded-lg p-3 space-y-1 list-disc list-inside">
          {errors.map((er, i) => <li key={i}>{er}</li>)}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-3">
        <F label="Legal business name (the SMS sender identity)"><input className="input" value={f.legal_name} onChange={e => set('legal_name', e.target.value)} placeholder="Montague Dental PLLC" /></F>
        <F label="Business type">
          <select className="input" value={f.business_type} onChange={e => set('business_type', e.target.value)}>
            {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </F>
        <F label="Federal EIN (9 digits)"><input className="input font-mono" value={f.ein} onChange={e => set('ein', e.target.value)} placeholder="12-3456789" /></F>
        <F label="Website"><input className="input" value={f.website_url} onChange={e => set('website_url', e.target.value)} placeholder="https://…" /></F>
        <F label="Street"><input className="input" value={f.street} onChange={e => set('street', e.target.value)} /></F>
        <F label="City"><input className="input" value={f.city} onChange={e => set('city', e.target.value)} /></F>
        <F label="State / region"><input className="input" value={f.region} onChange={e => set('region', e.target.value)} placeholder="MI" /></F>
        <F label="ZIP"><input className="input font-mono" value={f.postal_code} onChange={e => set('postal_code', e.target.value)} /></F>
        <F label="Authorized rep — first name"><input className="input" value={f.first_name} onChange={e => set('first_name', e.target.value)} /></F>
        <F label="Last name"><input className="input" value={f.last_name} onChange={e => set('last_name', e.target.value)} /></F>
        <F label="Rep email"><input className="input" value={f.email} onChange={e => set('email', e.target.value)} placeholder="owner@business.com" /></F>
        <F label="Rep phone (E.164)"><input className="input font-mono" value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="+1231…" /></F>
        <F label="New number area code (blank = account default)"><input className="input font-mono max-w-[120px]" maxLength={3} value={f.area_code} onChange={e => set('area_code', e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="989" /></F>
        <F label="…or transfer an existing number (PN SID, optional)"><input className="input font-mono" value={f.phone_number_sid} onChange={e => set('phone_number_sid', e.target.value)} placeholder="PN…" /></F>
      </div>
      <button type="button" className="btn-primary" disabled={busy} onClick={start}>{busy ? 'Registering…' : failed ? 'Retry registration' : 'Register brand + start texting setup'}</button>
    </div>
  );
}

// ── Cancel: park (keep number) vs full teardown (release, irreversible) ──────────
export function CancelSms({ domainUuid, fromNumber, smsEnabled, onDone }: { domainUuid?: string; fromNumber?: string; smsEnabled?: boolean; onDone?: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState('');
  if (!domainUuid || !smsEnabled) return null;

  async function cancel(mode: 'park' | 'teardown') {
    if (mode === 'teardown' && !window.confirm(`Release ${fromNumber || 'this number'} permanently? The number is lost and cannot be recovered. All Twilio cost for it stops.`)) return;
    setBusy(mode);
    try {
      const r = await jsend('/api/sms/teardown', 'POST', { domain_uuid: domainUuid, mode, ...(mode === 'teardown' ? { confirm_release: true } : {}) });
      toast('success', mode === 'park' ? 'SMS paused — number kept' : (r.released ? 'Number released — all cost stopped' : `Disabled; release note: ${r.release_error || 'see logs'}`));
      onDone && onDone();
    } catch (e: any) { toast('error', e.message); }
    finally { setBusy(''); }
  }

  return (
    <div className="card p-6 space-y-3">
      <div className="text-sm font-medium">Cancel texting (cost control)</div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn" disabled={!!busy} onClick={() => cancel('park')}>
          <PauseCircle size={14} className="inline mr-1" />{busy === 'park' ? 'Pausing…' : 'Park (keep number)'}
        </button>
        <button type="button" className="btn-danger" disabled={!!busy} onClick={() => cancel('teardown')}>
          <Trash2 size={14} className="inline mr-1" />{busy === 'teardown' ? 'Releasing…' : 'Release number (stop all cost)'}
        </button>
      </div>
      <p className="text-xs text-textSecondary">Park keeps the number (still billed) and can be re-enabled. Release frees the number permanently and ends its cost.</p>
    </div>
  );
}

// ── Master account vault (super-admin) — the Quick-lane source of numbers ────────
export function MasterConfigPanel() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const cfg = useQuery<any>({ queryKey: ['sms-master-config'], queryFn: () => jget('/api/sms/master-config'), enabled: open });
  const [form, setForm] = useState<any>(null);
  const d = cfg.data || {};
  const f = form || {
    account_sid: d.account_sid || '', api_key_sid: d.api_key_sid || '',
    messaging_service_sid: d.messaging_service_sid || '', default_area_code: d.default_area_code || '',
    enabled: !!d.enabled, api_secret: '', auth_token: '',
  };
  const set = (k: string, v: any) => setForm({ ...f, [k]: v });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const body: any = {
        account_sid: f.account_sid, api_key_sid: f.api_key_sid, messaging_service_sid: f.messaging_service_sid,
        default_area_code: f.default_area_code, enabled: f.enabled,
      };
      if (f.api_secret) body.api_secret = f.api_secret;
      if (f.auth_token) body.auth_token = f.auth_token;
      await jsend('/api/sms/master-config', 'PUT', body);
      toast('success', 'Master Twilio config saved');
      setForm({ ...f, api_secret: '', auth_token: '' });
      cfg.refetch();
    } catch (e: any) { toast('error', e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="card p-4">
      <button type="button" className="flex items-center gap-2 text-sm font-medium w-full" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        Master Twilio account (Quick-lane source)
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-textSecondary">The parent Twilio account new Quick-lane numbers are bought on and joined to the shared campaign. Secrets are encrypted and write-only — leave them blank to keep what's saved.</p>
          <div className="grid grid-cols-2 gap-3">
            <F label="Account SID"><input className="input font-mono" autoComplete="off" name="stratus-master-acct" value={f.account_sid} onChange={e => set('account_sid', e.target.value)} placeholder="AC…" /></F>
            <F label="Messaging Service SID"><input className="input font-mono" autoComplete="off" name="stratus-master-mg" value={f.messaging_service_sid} onChange={e => set('messaging_service_sid', e.target.value)} placeholder="MG…" /></F>
            <F label="API Key SID"><input className="input font-mono" autoComplete="off" name="stratus-master-sk" value={f.api_key_sid} onChange={e => set('api_key_sid', e.target.value)} placeholder="SK…" /></F>
            <F label={d.has_api_secret ? 'API Key Secret (saved — blank keeps it)' : 'API Key Secret'}>
              <input type="password" className="input font-mono" autoComplete="new-password" name="stratus-master-secret" value={f.api_secret} onChange={e => set('api_secret', e.target.value)} placeholder={d.has_api_secret ? '••••••••' : ''} />
            </F>
            <F label={d.has_auth_token ? 'Account Auth Token (saved — blank keeps it)' : 'Account Auth Token'}>
              <input type="password" className="input font-mono" autoComplete="new-password" name="stratus-master-token" value={f.auth_token} onChange={e => set('auth_token', e.target.value)} placeholder={d.has_auth_token ? '••••••••' : ''} />
            </F>
            <F label="Default area code"><input className="input font-mono max-w-[120px]" autoComplete="off" name="stratus-master-ac" maxLength={3} value={f.default_area_code} onChange={e => set('default_area_code', e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="989" /></F>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />Enabled (allow Quick-lane provisioning)</label>
          <button type="button" className="btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save master config'}</button>
        </div>
      )}
    </div>
  );
}
